/**
 * 策略工厂 - 根据歌曲类型选择合适的播放策略
 */
import { AudioStrategy } from './AudioStrategy.js';
import { VideoStrategy } from './VideoStrategy.js';
import { LyricsStrategy } from './LyricsStrategy.js';
import { AlbumStrategy } from './AlbumStrategy.js';

export class StrategyFactory {
  /**
   * 根据歌曲对象获取策略类
   * @param {Object} song - 歌曲对象
   * @returns {new () => PlayStrategy} 策略类构造函数
   */
  getStrategy(song) {
    if (!song) {
      console.warn('⚠️ [StrategyFactory] 歌曲对象为空，使用默认音频策略');
      return AudioStrategy;
    }
    
    // 判断歌曲类型
    // 优先级：视频版 > 图集版 > 歌词版 > 音频版
    
    // 1. 视频版：检查是否有 videoId
    //    - 如果 videoId 存在且不等于 id（说明是独立的视频文件），选择视频策略
    //    - 或者有 videoUrl，或者明确标记为 isVideoSong
    //    - 注意：videoId 可能为数字或字符串，需要正确比较
    
    // 🔥 修复：确保正确比较 videoId（处理类型转换）
    const songId = song.id;
    const videoId = song.videoId;
    
    // 检查是否为视频版歌曲
    const isVideoSong = (videoId != null && String(videoId) !== String(songId)) || 
                        song.videoUrl || 
                        song.isVideoSong === true;
    
    if (isVideoSong) {
      console.log('🎬 [StrategyFactory] 选择视频版策略:', song.title, {
        videoId: videoId,
        id: songId,
        videoUrl: song.videoUrl,
        isVideoSong: song.isVideoSong,
        videoIdType: typeof videoId,
        idType: typeof songId,
        videoIdNotEqualToId: videoId != null && String(videoId) !== String(songId)
      });
      return VideoStrategy;
    }
    
    // 2. 图集版：检查是否有 mediaAlbumId（不为 null）
    if (song.mediaAlbumId != null) {
      console.log('🖼️ [StrategyFactory] 选择图集版策略:', song.title, {
        mediaAlbumId: song.mediaAlbumId
      });
      return AlbumStrategy;
    }
    
    // 3. 歌词版：有有效的 lyricsUrl 或 lyrics 字段，或者是在线搜索的歌曲（一般都有歌词和音频）
    // 🔥 修复：只有当 lyricsUrl 是有效的非空字符串时才选择歌词版策略
    // 🚀 优化：在线搜索得到的歌曲一般有歌词也有音频，使用歌词版策略
    const isOnlineSong = song.isOnline || song.isExternalWithResources;
    const hasValidLyricsUrl = song.lyricsUrl && typeof song.lyricsUrl === 'string' && song.lyricsUrl.trim().length > 0;
    const hasLyricsContent = song.lyrics && (
      Array.isArray(song.lyrics) ? song.lyrics.length > 0 : 
      (typeof song.lyrics === 'string' && song.lyrics.trim().length > 0)
    );
    const isLyricsType = song.playbackType === 'lyrics';
    
    if (hasValidLyricsUrl || hasLyricsContent || isLyricsType || isOnlineSong) {
      if (isOnlineSong && !hasValidLyricsUrl && !hasLyricsContent) {
        console.log('🎵 [StrategyFactory] 在线搜索歌曲，使用歌词版策略（即使暂无歌词URL）:', song.title);
      } else {
        console.log('🎵 [StrategyFactory] 选择歌词版策略:', song.title, {
          hasValidLyricsUrl,
          hasLyricsContent,
          isLyricsType,
          isOnlineSong,
          lyricsUrl: song.lyricsUrl
        });
      }
      return LyricsStrategy;
    }
    
    // 4. 音频版：默认策略
    console.log('🎵 [StrategyFactory] 选择音频版策略:', song.title);
    return AudioStrategy;
  }
  
  /**
   * 根据策略类型字符串获取策略类
   * @param {string} type - 策略类型：'audio' | 'video' | 'lyrics' | 'album'
   * @returns {new () => PlayStrategy} 策略类构造函数
   */
  getStrategyByType(type) {
    switch (type) {
      case 'video':
        return VideoStrategy;
      case 'album':
        return AlbumStrategy;
      case 'lyrics':
        return LyricsStrategy;
      case 'audio':
      default:
        return AudioStrategy;
    }
  }
}

// 导出单例
export const strategyFactory = new StrategyFactory();

